import { DateTime } from "luxon";

const DATE_INPUT_DISPLAY_FORMAT = "dd.MM.yyyy";
const TWO_DIGIT_YEAR_PIVOT = 50;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const YEAR_ONLY_PATTERN = /^\d{4}$/;

export const DATE_ACCURACY_DAY = "DAN";
export const DATE_ACCURACY_MONTH = "MESEC";
export const DATE_ACCURACY_YEAR = "LETO";

const toNormalizedTwoDigitYear = (year) => {
  return year < TWO_DIGIT_YEAR_PIVOT ? 2000 + year : 1900 + year;
};

const splitSimplifiedDateParts = (value) => {
  const parts = value
    .trim()
    .split(/[.,/-]/)
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.length === 3 ? parts : null;
};

const normalizeDatePartsToISO = ({ day, month, year }) => {
  const normalizedYear = year < 100 ? toNormalizedTwoDigitYear(year) : year;
  const date = DateTime.fromObject({
    year: normalizedYear,
    month,
    day,
  });

  if (!date.isValid) {
    return null;
  }

  return date.toISODate();
};

const parseMonthYearInputValue = (value) => {
  const parts = value
    .trim()
    .split(/[.,/-]/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length !== 2) {
    return null;
  }

  const [monthPart, yearPart] = parts;
  if (!/^\d{1,2}$/.test(monthPart) || !/^\d{4}$/.test(yearPart)) {
    return null;
  }

  const date = DateTime.fromObject({
    year: Number(yearPart),
    month: Number(monthPart),
    day: 1,
  });

  if (!date.isValid) {
    return null;
  }

  return {
    normalizedValue: date.toISODate(),
    accuracy: DATE_ACCURACY_MONTH,
  };
};

export const normalizeDateAccuracyValue = (value) => {
  return [DATE_ACCURACY_DAY, DATE_ACCURACY_MONTH, DATE_ACCURACY_YEAR].includes(
    value
  )
    ? value
    : DATE_ACCURACY_DAY;
};

/**
 * Used to properly transform date values or return null for empty/invalid dates for validation schemas primarily. ( example. PhysicalAssessments)
 * @param value
 * @param originalValue
 * @returns {null|Date|Date}
 */

export const toDateOrNull = (_value, originalValue) => {
  // Unwrap arrays like ["2026-01-19"] or ["undefined"]
  const raw = Array.isArray(originalValue) ? originalValue[0] : originalValue;

  if (raw === "" || raw == null || raw === "undefined") return null;

  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Converts supported date input formats into canonical ISO date strings.
 * Supported inputs:
 * - Date objects
 * - ISO dates (yyyy-MM-dd)
 * - Standard localized dates (dd.MM.yyyy / dd.MM.yy)
 * - Simplified keypad input (d,m,yy / d,m,yyyy)
 *
 * @param {string|Date|null|undefined} value
 * @returns {string|null}
 */
export const normalizeDateInputValue = (value) => {
  if (value == null || value === "") {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }

    return DateTime.fromJSDate(value).toISODate();
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  if (trimmedValue === "") {
    return null;
  }

  const isoDate = DateTime.fromISO(trimmedValue);
  if (isoDate.isValid && ISO_DATE_PATTERN.test(trimmedValue)) {
    return isoDate.toISODate();
  }

  const dateParts = splitSimplifiedDateParts(trimmedValue);
  if (!dateParts) {
    return null;
  }

  const [dayPart, monthPart, yearPart] = dateParts;
  if (
    !/^\d{1,2}$/.test(dayPart) ||
    !/^\d{1,2}$/.test(monthPart) ||
    !/^(\d{2}|\d{4})$/.test(yearPart)
  ) {
    return null;
  }

  return normalizeDatePartsToISO({
    day: Number(dayPart),
    month: Number(monthPart),
    year: Number(yearPart),
  });
};

/**
 * Determines whether a typed value should be normalized immediately while the
 * user is still typing. Two-digit years are intentionally deferred until blur.
 *
 * @param {string|Date|null|undefined} value
 * @returns {boolean}
 */
export const canAutoNormalizeDateInputValue = (value) => {
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  if (typeof value !== "string") {
    return false;
  }

  const trimmedValue = value.trim();
  if (trimmedValue === "") {
    return true;
  }

  if (ISO_DATE_PATTERN.test(trimmedValue)) {
    return true;
  }

  const dateParts = splitSimplifiedDateParts(trimmedValue);
  return dateParts ? dateParts[2].length === 4 : false;
};

export const normalizeDateValueWithAccuracy = (value) => {
  if (value == null || value === "") {
    return null;
  }

  if (value instanceof Date) {
    const normalizedValue = normalizeDateInputValue(value);

    return normalizedValue
      ? { normalizedValue, accuracy: DATE_ACCURACY_DAY }
      : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  if (trimmedValue === "") {
    return null;
  }

  if (YEAR_ONLY_PATTERN.test(trimmedValue)) {
    return {
      normalizedValue: `${trimmedValue}-01-01`,
      accuracy: DATE_ACCURACY_YEAR,
    };
  }

  const monthYearValue = parseMonthYearInputValue(trimmedValue);
  if (monthYearValue) {
    return monthYearValue;
  }

  const normalizedValue = normalizeDateInputValue(trimmedValue);
  return normalizedValue
    ? { normalizedValue, accuracy: DATE_ACCURACY_DAY }
    : null;
};

export const canAutoNormalizeDateValueWithAccuracy = (value) => {
  if (value instanceof Date) {
    return true;
  }

  if (typeof value !== "string") {
    return false;
  }

  const trimmedValue = value.trim();
  if (trimmedValue === "") {
    return true;
  }

  return (
    YEAR_ONLY_PATTERN.test(trimmedValue) ||
    !!parseMonthYearInputValue(trimmedValue) ||
    canAutoNormalizeDateInputValue(trimmedValue)
  );
};

/**
 * Formats a supported date input value to the standard display format dd.MM.yyyy.
 *
 * @param {string|Date|null|undefined} value
 * @param {string} fallback
 * @returns {string}
 */
export const formatDateInputDisplayValue = (value, fallback = "") => {
  const isoDate = normalizeDateInputValue(value);
  if (!isoDate) {
    return fallback;
  }

  return DateTime.fromISO(isoDate).toFormat(DATE_INPUT_DISPLAY_FORMAT);
};

export const formatDateInputDisplayValueByAccuracy = (
  value,
  accuracy = DATE_ACCURACY_DAY,
  fallback = ""
) => {
  const normalizedValue = normalizeDateInputValue(value);
  if (!normalizedValue) {
    return fallback;
  }

  const date = DateTime.fromISO(normalizedValue);
  if (!date.isValid) {
    return fallback;
  }

  switch (normalizeDateAccuracyValue(accuracy)) {
    case DATE_ACCURACY_YEAR:
      return date.toFormat("yyyy");
    case DATE_ACCURACY_MONTH:
      return date.toFormat("MM.yyyy");
    default:
      return formatDateInputDisplayValue(normalizedValue, fallback);
  }
};

/**
 * Formats an ISO date string to locale short date format (e.g., "1/15/2024")
 * @param {string} isoDateString - ISO date string (e.g., "2024-01-15T00:00:00")
 * @param {string} fallback - Fallback value if date is invalid or missing (default: "-")
 * @returns {string} Formatted date string or fallback
 */
export const formatISODateToShort = (isoDateString, fallback = "-") => {
  if (!isoDateString) return fallback;

  try {
    return DateTime.fromISO(isoDateString).toLocaleString(DateTime.DATE_SHORT, {
      locale: "sl",
    });
  } catch {
    return fallback;
  }
};

/**
 * Formats a date value (ISO string or Date object) to locale short date format
 * Returns fallback if date is null, undefined, or invalid
 * @param {string|Date} dateValue - Date value to format
 * @param {string} fallback - Fallback value if date is invalid or missing (default: "-")
 * @returns {string} Formatted date string or fallback
 */
export const formatDateOrFallback = (dateValue, fallback = "-") => {
  if (!dateValue) return fallback;
  return formatISODateToShort(dateValue, fallback);
};

/**
 * Returns date object or null resetting time to 0.
 * @param {string|Date} date - Date value to convert
 */
export const toDateOnly = (date) => {
  if (!date) {
    return null;
  }
  if (date instanceof Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
  return toDateOnly(new Date(date));
};

/**
 * Adds or subtracts months from a date, returning a new date object with time reset to 0.
 * @param {string|Date} date - Date value to convert
 * @param {number} months - Number of months to add or subtract
 */
export const addMonths = (date, months) => {
  const copy = toDateOnly(date);
  if (copy == null) {
    return null;
  }

  copy.setMonth(copy.getMonth() + months);
  return copy;
};

/**
 * Checks if a date is before a certain number of months ago from a reference date (default: now).
 * @param {string|Date} date - Date value to convert
 * @param {number} months - Number of months to subtract
 * @param {Date} referenceDate - Reference date to compare against (default: now)
 * @returns {boolean}
 */
export const isDateBeforeMonthsAgo = (
  date,
  months,
  referenceDate = new Date()
) => {
  const normalizedDate = toDateOnly(date);
  const minAllowedDate = addMonths(referenceDate, -months);

  if (normalizedDate == null || minAllowedDate == null) {
    return false;
  }

  return normalizedDate.getTime() < minAllowedDate.getTime();
};

/**
 * Checks if a date is between two other dates (inclusive).
 *
 * @param {string|Date} date - Date value to convert
 * @param {string|Date} minDate - Minimum date to compare against
 * @param {string|Date} maxDate - Maximum date to compare against
 * @returns {boolean}
 */
export const isDateBetweenInclusive = (date, minDate, maxDate) => {
  const normalizedDate = toDateOnly(date);
  const normalizedMinDate = toDateOnly(minDate);
  const normalizedMaxDate = toDateOnly(maxDate);

  if (
    normalizedDate == null ||
    normalizedMinDate == null ||
    normalizedMaxDate == null
  ) {
    return false;
  }

  return (
    normalizedDate.getTime() >= normalizedMinDate.getTime() &&
    normalizedDate.getTime() <= normalizedMaxDate.getTime()
  );
};
